"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Image as ImageIcon,
  Layers,
  Loader2,
  MessageCircle,
  Play,
  Plus,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ActivitySessionFeedItem,
  getActivitySessionsFeed,
} from "@/actions/activity-session";
import type { FeedPost } from "@/actions/post";
import type { SocialFeedItem as SocialFeedItemData } from "@/actions/social-feed";
import type { UserReplyItem } from "@/actions/user-replies";
import { ActivityPost } from "@/components/feed/activity-post";
import { ActivitySessionCard } from "@/components/feed/activity-session-card/activity-session-card.client";
import { ActivitySessionDrawer } from "@/components/feed/activity-session-drawer";
import { RegularPostDrawer } from "@/components/feed/regular-post-drawer";
import { SocialFeedItemCard } from "@/components/feed/social-feed-item";
import { Post } from "@/components/posts/post";
import { AppImage } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { FilterRail, FilterRailItem } from "@/components/ui/filter-rail";
import { ListRowSkeleton } from "@/components/ui/list-row-skeleton";
import { useActivityFeed } from "@/hooks/use-activity-feed";
import { usePostFeed } from "@/hooks/use-posts";
import { useSocialFeed } from "@/hooks/use-social-feed";
import { useUserReplies } from "@/hooks/use-user-replies";
import type { ChronologicalFeedCursor } from "@/lib/feed/chronological-pagination";
import { toRenderableMediaArray } from "@/lib/media";
import type { ProfileBuzzFilter } from "@/lib/profile-navigation";
import { PROFILE_TAB_POST_LIMIT } from "@/lib/query-keys/profile-tabs";
import type { CommentsSheetViewer } from "../posts/comments-sheet";
import { ProfileBuzzSkeleton } from "./profile-buzz.skeleton";

import { ProfileReplyRow } from "./profile-reply-row";

const ProfileActivitiesTab = dynamic(
  () =>
    import("./profile-activities-tab").then(
      (module) => module.ProfileActivitiesTab,
    ),
  { loading: () => <ListRowSkeleton rowCount={4} className="py-2" /> },
);

type ProfilePostsTabProps = {
  userId: string;
  viewer?: CommentsSheetViewer;
  view: ProfileBuzzFilter;
  onViewChange: (view: ProfileBuzzFilter) => void;
};

const VIEWS: Array<{ value: ProfileBuzzFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "posts", label: "Posts" },
  { value: "sessions", label: "Sessions" },
  { value: "replies", label: "Replies" },
  { value: "media", label: "Media" },
  { value: "stats", label: "Stats" },
];

type UnifiedItem =
  | {
      type: "social";
      id: string;
      createdAt: number;
      value: SocialFeedItemData;
    }
  | {
      type: "session";
      id: number;
      createdAt: Date | number;
      value: ActivitySessionFeedItem;
    }
  | {
      type: "reply";
      id: string;
      createdAt: Date | number;
      value: UserReplyItem;
    }
  | {
      type: "post";
      id: number;
      createdAt: Date | number;
      value: Parameters<typeof Post>[0]["post"];
    }
  | {
      type: "activity";
      id: number;
      createdAt: Date | number;
      value: Parameters<typeof ActivityPost>[0]["post"];
    };

export function ProfilePostsTab({
  userId,
  viewer,
  view,
  onViewChange,
}: ProfilePostsTabProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sessionComposerOpen, setSessionComposerOpen] = useState(false);
  const currentUser =
    viewer?.id === userId
      ? {
          id: viewer.id,
          displayName: viewer.displayName ?? viewer.username ?? "You",
          avatarUrl: viewer.avatarUrl,
        }
      : null;

  const isMedia = view === "media";
  const showPosts = view === "all" || view === "posts";
  const showSessions = view === "all" || view === "sessions";
  const showReplies = view === "all" || view === "replies";
  const sessionsQuery = useInfiniteQuery({
    queryKey: ["activitySessions", "profile", userId],
    enabled: showSessions,
    initialPageParam: null as ChronologicalFeedCursor | null,
    queryFn: async ({ pageParam }) => {
      const result = await getActivitySessionsFeed({
        userId,
        includeCompleted: true,
        chronological: true,
        chronologicalCursor: pageParam ?? undefined,
        limit: PROFILE_TAB_POST_LIMIT,
      });
      if (!result.ok)
        throw new Error(result.message ?? "Unable to load sessions");
      return result.data;
    },
    getNextPageParam: (page) => page.chronologicalNextCursor ?? undefined,
  });
  const regularPostsQuery = usePostFeed(
    { type: "user", userId, includePrivatePreview: true },
    PROFILE_TAB_POST_LIMIT,
    undefined,
    { enabled: showPosts },
  );
  const mediaPostsQuery = usePostFeed(
    { type: "user", userId, mediaOnly: true },
    PROFILE_TAB_MEDIA_LIMIT,
    undefined,
    { enabled: isMedia },
  );
  const activityPostsQuery = useActivityFeed(
    PROFILE_TAB_POST_LIMIT,
    undefined,
    {
      userId,
      includePrivatePreview: true,
      enabled: showPosts,
    },
  );
  const socialQuery = useSocialFeed(PROFILE_TAB_POST_LIMIT, undefined, {
    userId,
    enabled: showPosts,
  });
  const repliesQuery = useUserReplies(userId, {
    enabled: showReplies,
  });

  const regularPosts =
    regularPostsQuery.data?.pages.flatMap((page) => page.posts) ?? [];
  const activityPosts =
    activityPostsQuery.data?.pages.flatMap((page) => page.posts) ?? [];
  const replies =
    repliesQuery.data?.pages.flatMap((page) => page.replies) ?? [];
  const mediaPosts =
    mediaPostsQuery.data?.pages.flatMap((page) => page.posts) ?? [];

  const sources = useMemo(() => {
    const list: Array<{
      items: UnifiedItem[];
      hasNextPage: boolean;
      fetchNextPage: () => Promise<unknown>;
    }> = [
      {
        items: regularPosts.map((post) => ({
          type: "post" as const,
          id: post.id,
          createdAt: post.createdAt,
          value: post,
        })),
        hasNextPage: Boolean(regularPostsQuery.hasNextPage),
        fetchNextPage: regularPostsQuery.fetchNextPage,
      },
      {
        items: activityPosts.map((post) => ({
          type: "activity" as const,
          id: post.id,
          createdAt: post.createdAt,
          value: post,
        })),
        hasNextPage: Boolean(activityPostsQuery.hasNextPage),
        fetchNextPage: activityPostsQuery.fetchNextPage,
      },
    ];
    list.push({
      items: (socialQuery.data?.pages.flatMap((page) => page.items) ?? []).map(
        (item) => ({
          type: "social" as const,
          id: `${item.entityType}-${item.entityId}`,
          createdAt: item.createdAt,
          value: item,
        }),
      ),
      hasNextPage: Boolean(socialQuery.hasNextPage),
      fetchNextPage: socialQuery.fetchNextPage,
    });
    if (!showPosts) list.length = 0;
    if (showSessions)
      list.push({
        items: (
          sessionsQuery.data?.pages.flatMap((page) => page.events) ?? []
        ).map((event) => ({
          type: "session" as const,
          id: event.id,
          createdAt: event.createdAt,
          value: event,
        })),
        hasNextPage: Boolean(sessionsQuery.hasNextPage),
        fetchNextPage: sessionsQuery.fetchNextPage,
      });
    if (showReplies) {
      list.push({
        items: replies.map((reply) => ({
          type: "reply" as const,
          id: reply.key,
          createdAt: reply.createdAt,
          value: reply,
        })),
        hasNextPage: Boolean(repliesQuery.hasNextPage),
        fetchNextPage: repliesQuery.fetchNextPage,
      });
    }
    return list;
  }, [
    socialQuery.data,
    socialQuery.hasNextPage,
    socialQuery.fetchNextPage,
    showPosts,
    showSessions,
    showReplies,
    sessionsQuery.data,
    sessionsQuery.hasNextPage,
    sessionsQuery.fetchNextPage,
    regularPosts,
    activityPosts,
    replies,
    regularPostsQuery.hasNextPage,
    regularPostsQuery.fetchNextPage,
    activityPostsQuery.hasNextPage,
    activityPostsQuery.fetchNextPage,
    repliesQuery.hasNextPage,
    repliesQuery.fetchNextPage,
  ]);

  const items = useMemo<UnifiedItem[]>(() => {
    // Each source pages on its own, so an item older than another source's
    // last loaded item may still have newer company on that source's next
    // page. Hold it back until then, or the list reshuffles as pages land.
    const cutoff = Math.max(
      ...sources
        .filter((source) => source.hasNextPage && source.items.length > 0)
        .map((source) => timeOf(source.items.at(-1)?.createdAt ?? 0)),
      Number.NEGATIVE_INFINITY,
    );
    return sources
      .flatMap((source) => source.items)
      .filter((item) => timeOf(item.createdAt) >= cutoff)
      .sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt));
  }, [sources]);

  const isLoading = isMedia
    ? mediaPostsQuery.isLoading
    : (showPosts &&
        (regularPostsQuery.isLoading ||
          activityPostsQuery.isLoading ||
          socialQuery.isLoading)) ||
      (showSessions && sessionsQuery.isLoading) ||
      (showReplies && repliesQuery.isLoading);
  const hasNextPage = isMedia
    ? Boolean(mediaPostsQuery.hasNextPage)
    : sources.some((source) => source.hasNextPage);
  const isFetchingNextPage = isMedia
    ? mediaPostsQuery.isFetchingNextPage
    : (showPosts &&
        (regularPostsQuery.isFetchingNextPage ||
          activityPostsQuery.isFetchingNextPage ||
          socialQuery.isFetchingNextPage)) ||
      (showSessions && sessionsQuery.isFetchingNextPage) ||
      (showReplies && repliesQuery.isFetchingNextPage);

  const loadMore = useCallback(async () => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }
    if (isMedia) {
      await mediaPostsQuery.fetchNextPage();
      return;
    }
    await Promise.allSettled(
      sources
        .filter((source) => source.hasNextPage)
        .map((source) => source.fetchNextPage()),
    );
  }, [hasNextPage, isFetchingNextPage, isMedia, mediaPostsQuery, sources]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          void loadMore();
        }
      });
    });

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, loadMore]);

  const error = isMedia
    ? mediaPostsQuery.error
    : ((showPosts
        ? (regularPostsQuery.error ??
          activityPostsQuery.error ??
          socialQuery.error)
        : null) ??
      (showSessions ? sessionsQuery.error : null) ??
      (showReplies ? repliesQuery.error : null));

  const viewRail = (
    <FilterRail
      className="px-3 pt-3 pb-2"
      restoreKey={`profile-posts-${userId}`}
    >
      {VIEWS.map((option) => (
        <FilterRailItem
          key={option.value}
          size="chip"
          theme="light"
          label={option.label}
          active={view === option.value}
          onClick={() => onViewChange(option.value)}
        />
      ))}
    </FilterRail>
  );

  let content: React.ReactNode;
  if (view === "stats") {
    content = (
      <ProfileActivitiesTab
        userId={userId}
        isCurrentUser={Boolean(currentUser)}
      />
    );
  } else if (error) {
    const message =
      error instanceof Error ? error.message : "Unable to load posts";
    content = (
      <div className="border-y border-border px-4 py-6">
        <p className="text-sm text-text-secondary">{message}</p>
        <Button
          type="button"
          variant="outline"
          className="mt-3"
          onClick={() => {
            if (isMedia) {
              void mediaPostsQuery.refetch();
              return;
            }
            if (showPosts) {
              void regularPostsQuery.refetch();
              void activityPostsQuery.refetch();
              void socialQuery.refetch();
            }
            if (showReplies) void repliesQuery.refetch();
            if (showSessions) void sessionsQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  } else if (isLoading) {
    content = <ProfileBuzzSkeleton view={view} />;
  } else if (isMedia) {
    content =
      mediaPosts.length === 0 ? (
        <div className="border-y border-border">
          <EmptyState
            icon={ImageIcon}
            title="No photos or videos yet"
            description={
              currentUser
                ? "Posts with photos and videos show up here."
                : "This profile hasn't shared any photos or videos."
            }
          />
        </div>
      ) : (
        <>
          <ProfileMediaGrid posts={mediaPosts} />
          <div ref={sentinelRef} />
          {hasNextPage ? (
            <LoadMoreRow loading={isFetchingNextPage} onLoadMore={loadMore} />
          ) : null}
        </>
      );
  } else if (items.length === 0) {
    content = (
      <div className="border-y border-border">
        <EmptyState
          icon={view === "replies" ? MessageCircle : ImageIcon}
          title={
            view === "sessions"
              ? "No sessions yet"
              : view === "replies"
                ? "No replies yet"
                : "Nothing shared yet"
          }
          description={
            view === "sessions"
              ? "Sessions hosted or joined appear here."
              : view === "replies"
                ? "Replies to posts and discussions appear here."
                : "Posts and moments shared with buddies appear here."
          }
          action={
            currentUser ? (
              <Button
                type="button"
                onClick={() =>
                  view === "sessions"
                    ? setSessionComposerOpen(true)
                    : setComposerOpen(true)
                }
              >
                <Plus className="h-4 w-4" />
                {view === "sessions" ? "Plan a session" : "Create a post"}
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  } else {
    content = (
      <div className="space-y-0 divide-y divide-border">
        {items.map((item) =>
          item.type === "session" ? (
            <ActivitySessionCard
              key={`session-${item.id}`}
              event={item.value}
              viewer={viewer}
            />
          ) : item.type === "post" ? (
            <Post key={`post-${item.id}`} post={item.value} viewer={viewer} />
          ) : item.type === "social" ? (
            <SocialFeedItemCard
              key={`social-${item.id}`}
              item={item.value}
              viewer={viewer}
            />
          ) : item.type === "reply" ? (
            <ProfileReplyRow key={`reply-${item.id}`} reply={item.value} />
          ) : (
            <ActivityPost
              key={`activity-${item.id}`}
              post={item.value}
              viewer={viewer}
            />
          ),
        )}

        <div ref={sentinelRef} />

        {hasNextPage ? (
          <LoadMoreRow loading={isFetchingNextPage} onLoadMore={loadMore} />
        ) : null}
      </div>
    );
  }

  return (
    <>
      {viewRail}
      {content}
      {currentUser && (
        <RegularPostDrawer
          open={composerOpen}
          onOpenChange={setComposerOpen}
          currentUser={currentUser}
          onPlanSession={() => setSessionComposerOpen(true)}
        />
      )}
      {currentUser && (
        <ActivitySessionDrawer
          open={sessionComposerOpen}
          onOpenChange={setSessionComposerOpen}
          currentUser={currentUser}
          onSwitchToPost={() => setComposerOpen(true)}
        />
      )}
    </>
  );
}

const PROFILE_TAB_MEDIA_LIMIT = 24;

const timeOf = (value: Date | number) => new Date(value).getTime();

function LoadMoreRow({
  loading,
  onLoadMore,
}: {
  loading: boolean;
  onLoadMore: () => void;
}) {
  return (
    <div className="flex justify-center py-2">
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-text-secondary" />
      ) : (
        <Button type="button" variant="ghost" onClick={() => void onLoadMore()}>
          Load more
        </Button>
      )}
    </div>
  );
}

/** X/Instagram media tab: a square grid, one tile per post. */
function ProfileMediaGrid({ posts }: { posts: FeedPost[] }) {
  return (
    <div className="grid grid-cols-3 gap-0.5">
      {posts.map((post) => {
        const media = toRenderableMediaArray(post.media);
        const first = media[0];
        const thumb =
          first?.kind === "video" ? (first.poster?.url ?? null) : first?.url;
        if (!first || !thumb) return null;
        return (
          <BackAwareLink
            key={post.id}
            href={`/post/${post.id}`}
            aria-label={`Open post${post.content ? `: ${post.content}` : ""}`}
            className="relative block aspect-square overflow-hidden bg-surface-alt"
          >
            <AppImage
              src={thumb}
              alt=""
              fill
              sizes="(max-width: 640px) 33vw, 220px"
              className="object-cover"
              imageContext="feed"
            />
            {media.length > 1 ? (
              <Layers
                aria-hidden="true"
                className="absolute top-1.5 right-1.5 h-4 w-4 text-white drop-shadow"
              />
            ) : first.kind === "video" ? (
              <Play
                aria-hidden="true"
                className="absolute top-1.5 right-1.5 h-4 w-4 fill-white text-white drop-shadow"
              />
            ) : null}
          </BackAwareLink>
        );
      })}
    </div>
  );
}
